import { useApolloClient } from '@apollo/client';
import { useEffect, useRef, useState, useCallback } from 'react';

import { useAppConfig } from 'src/modules/appConfig';
import { browserLogError } from 'src/modules/error-logger/browserLog';
import { GetProfileAvatarStatusDocument } from 'src/types/graphql';

import type {
  ProfileAvatarStatusTypename,
  ProfileAvatarStatusFailureError,
} from '../types';

// Polling timing constants
export const POLLING_TIMINGS = {
  INITIAL_DELAY: 3000, // 3 seconds delay after upload before first poll
  SUBSEQUENT_DELAY: 1000, // 1 second between each subsequent poll
  MAX_POLL_ATTEMPTS: 30, // 30 seconds total polling time
} as const;

export type PollingState = {
  status: ProfileAvatarStatusTypename | undefined;
  pollAttempts: number;
  isPolling: boolean;
  timeout: boolean;
  profileAvatarStatusFailureError: ProfileAvatarStatusFailureError | undefined;
};

export const usePollAvatarStatus = () => {
  const apolloClient = useApolloClient();
  const { language, locale } = useAppConfig();
  const languageLocale = `${language}-${locale}`;
  const avatarIdRef = useRef<string | undefined>(undefined);

  const [pollingState, setPollingState] = useState<PollingState>({
    status: undefined,
    pollAttempts: 0,
    isPolling: false,
    timeout: false,
    profileAvatarStatusFailureError: undefined,
  });

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const pollStatusRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    const avatarId = avatarIdRef.current;
    if (!avatarId) {
      return;
    }

    let result;
    try {
      result = await apolloClient.query({
        query: GetProfileAvatarStatusDocument,
        variables: {
          avatarId,
          locale: languageLocale,
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      });
    } catch (error) {
      // Unexpected error from query - stop polling
      if (error instanceof Error) {
        browserLogError(error, {
          kind: 'GraphQueryError',
          name: 'AvatarStatusPollingError',
          input: {
            avatarId,
            locale: languageLocale,
          },
        });
      }
      clearTimeoutRef();
      setPollingState((prev) => ({
        ...prev,
        isPolling: false,
      }));
      return;
    }

    const profileAvatarStatusResponse =
      result.data?.viewer?.profileAvatarStatus;
    const status = profileAvatarStatusResponse?.__typename;

    if (status === 'ProfileAvatarStatusPending') {
      setPollingState((prev) => {
        const nextAttempt = prev.pollAttempts + 1;

        if (nextAttempt <= POLLING_TIMINGS.MAX_POLL_ATTEMPTS) {
          // Schedule next poll
          timeoutRef.current = setTimeout(() => {
            pollStatusRef.current?.();
          }, POLLING_TIMINGS.SUBSEQUENT_DELAY);

          return {
            ...prev,
            status,
            pollAttempts: nextAttempt,
          };
        }
        // Max attempts reached - timeout
        return {
          ...prev,
          timeout: true,
          status,
          isPolling: false,
        };
      });
    } else {
      // Polling complete; update status and error states and let consumer handle result
      const profileAvatarStatusFailureError =
        status === 'ProfileAvatarStatusFailure'
          ? profileAvatarStatusResponse?.error
          : undefined;

      // Log failure status for monitoring
      if (profileAvatarStatusFailureError) {
        browserLogError(new Error('Avatar polling failure'), {
          kind: 'GraphQueryError',
          name: 'AvatarStatusPollingFailure',
          input: {
            avatarId: avatarIdRef.current,
            locale: languageLocale,
            errorType: profileAvatarStatusFailureError.__typename,
            errorMessage: profileAvatarStatusFailureError.message,
            errorName: profileAvatarStatusFailureError.title,
          },
        });
      }

      clearTimeoutRef();

      setPollingState((prev) => ({
        ...prev,
        status,
        isPolling: false,
        profileAvatarStatusFailureError,
      }));
    }
  }, [apolloClient, languageLocale, clearTimeoutRef]);

  // Keep ref updated with latest pollStatus function for recursive calls
  useEffect(() => {
    pollStatusRef.current = pollStatus;
  }, [pollStatus]);

  const startPolling = useCallback((avatarId: string) => {
    avatarIdRef.current = avatarId;

    setPollingState((prev) => ({
      ...prev,
      isPolling: true,
      pollAttempts: 0,
      timeout: false,
      profileAvatarStatusFailureError: undefined,
    }));

    timeoutRef.current = setTimeout(() => {
      pollStatusRef.current?.();
    }, POLLING_TIMINGS.INITIAL_DELAY);
  }, []);

  // Cleanup on unmount
  useEffect(
    () => () => {
      clearTimeoutRef();
    },
    [clearTimeoutRef],
  );

  return {
    ...pollingState,
    startPolling,
  };
};
